'use client';

// Core
import { useState } from 'react';

// UI components
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Vendors
import { useTheme } from 'next-themes';
import { useTranslations } from '@/hooks/useTranslations';
import { LogOut, ChevronsUpDown } from 'lucide-react';

// Auth components
import { LogoutButton } from '@/features/auth/logout-button';

// Local components
import { AccountMenu } from './account-menu';
import { LanguageMenu } from './language-menu';
import { ThemeMenu } from './theme-menu';
import { ExtendedUser } from '@/types/next-auth';

export default function AvatarContainer({ user }: { user: ExtendedUser }) {
  const [isOpen, setIsOpen] = useState(false);
  const t = useTranslations('Navigation');
  const { setTheme } = useTheme();

  return (
    <div className="hidden sm:flex">
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`${user.name} account menu`}
            className="flex gap-3 cursor-pointer"
          >
            <Avatar className="min-h-10 min-w-10 border">
              <AvatarImage src={user.image!} alt={user.name!} />
              <AvatarFallback className="text-md font-semibold">
                {user
                  .name!.split(' ')
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()}
              </AvatarFallback>
            </Avatar>

            <div className="hidden lg:flex items-center gap-1">
              <div className="flex flex-col items-start">
                <span className="text-sm font-medium truncate max-w-40">
                  {user.name}
                </span>
                {user.email && (
                  <span className="text-xs text-muted-foreground truncate max-w-40">
                    {user.email}
                  </span>
                )}
              </div>
              <ChevronsUpDown
                className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                  isOpen ? 'rotate-180' : ''
                }`}
              />
            </div>
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent className="min-w-48 -translate-x-12">
          <AccountMenu t={t} user={user} />

          <LanguageMenu />

          <ThemeMenu setTheme={setTheme} />

          <DropdownMenuSeparator />

          <LogoutButton>
            <DropdownMenuItem aria-label="Log out" className="cursor-pointer">
              <LogOut className="h-4 w-4" color="#787B80" />
              <span className="text-sm font-medium">{t('logOut')}</span>
            </DropdownMenuItem>
          </LogoutButton>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
